import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { Router } from '@angular/router'
import { SideNavItem } from '@modules/navigation/models'
import { NavigationService, SideNavService } from '@modules/navigation/services'
import objectHash from 'object-hash'
import { Subscription } from 'rxjs'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { IconName } from '@fortawesome/fontawesome-common-types'
import { IconPrefix } from '@fortawesome/free-regular-svg-icons'

@Component({
	selector: 'sbpro-side-nav-item',
	templateUrl: './side-nav-item-pro.component.html',
	styleUrls: ['side-nav-item-pro.component.scss'],
	standalone: false
})
export class SideNavItemProComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('collapsibleSection')
	collapsibleSection!: ElementRef<HTMLDivElement>

	@Input() sideNavItem!: SideNavItem
	@Input() hierarchy: string[] = []

	id!: string
	collapsed!: boolean
	isActive = false
	navElement!: HTMLDivElement
	hierarchyExtension!: string[]
	afterViewInit = false
	subscription: Subscription = new Subscription()

	constructor(
		private router: Router,
		private sideNavService: SideNavService,
		public navigationService: NavigationService,
		private changeDetectorRef: ChangeDetectorRef
	) {}

	ngOnInit() {
		this.sideNavService.clearCache()

		this.id = objectHash(this.sideNavItem)
		this.hierarchyExtension = [...this.hierarchy, this.id]

		if (this.sideNavItem.text === 'Dashboards') {
			this.sideNavService.setDefault(this.hierarchyExtension)
		}

		this.collapsed = !this.sideNavService.isExpanded(this.id)

		this.subscription.add(
			this.sideNavService.expand$.subscribe(ids => {
				const thisIDisInExpandedHierarchy = !!ids.find(id => id === this.id)

				const sameLevel = ids.length === this.hierarchyExtension.length

				if (!thisIDisInExpandedHierarchy && sameLevel) {
					if (!this.collapsed) {
						this.collapse()
					}
				}
			})
		)

		this.subscription.add(
			this.navigationService.currentURL$().subscribe(currentURL => this.determineIfActive(currentURL.replace(/#.*$/, ''), this.sideNavItem))
		)
	}

	ngAfterViewInit() {
		this.afterViewInit = true
		this.navElement = this.collapsibleSection.nativeElement

		this.navElement.addEventListener('transitionend', _ => {
			if (!this.collapsed) {
				this.navElement.style.height = ''
			}
		})

		if (this.isActive) {
			this.expand()
		}

		this.changeDetectorRef.detectChanges()
	}

	ngOnDestroy() {
		this.subscription.unsubscribe()
	}

	determineIfActive(url: string, sideNavItem: SideNavItem) {
		url = this.normalizeUrl(url)
		url = url.includes('edit') ? url.substring(0, url.indexOf('/edit')) + '/register' : url

		if (url.includes('/pca/board')) {
			url = url.substring(0, url.indexOf('/pca/board')) + '/pca/board'
		}

		if (url.includes('/price-research/board')) {
			url = url.substring(0, url.indexOf('/price-research/board')) + '/price-research/board'
		}

		if (url.includes('/planning/board')) {
			url = url.substring(0, url.indexOf('/planning/board')) + '/planning/board'
		}

		if (url.includes('/planning/auxiliary-procedures')) {
			url = url.substring(0, url.indexOf('/planning/auxiliary-procedures')) + '/planning/auxiliary-procedures'
		}

		if (url.includes('/planning/electronic-processes/board')) {
			url = url.substring(0, url.indexOf('/planning/electronic-processes/board')) + '/planning/electronic-processes/board'
		}

		if (url.includes('/planning/bidding-procedures')) {
			url = url.substring(0, url.indexOf('/planning/bidding-procedures')) + '/planning/bidding-procedures'
		}

		if (url.includes('/planning/bidding-procedures/board')) {
			url = url.substring(0, url.indexOf('/planning/bidding-procedures/board')) + '/planning/bidding-procedures/board'
		}

		if (url.includes('/contracts/board')) {
			url = url.substring(0, url.indexOf('/contracts/board')) + '/contracts/board'
		}

		if (url.includes('/involving-registration-prices/board')) {
			url = url.substring(0, url.indexOf('/involving-registration-prices/board')) + '/involving-registration-prices/board'
		}

		this.isActive = false
		this.changeDetectorRef.markForCheck()

		if (sideNavItem.link) {
			this.isActive = url === this.normalizeUrl(sideNavItem.link)
			this.changeDetectorRef.markForCheck()
		}

		if (sideNavItem.submenu) {
			sideNavItem.submenu.forEach(submenu => this.determineIfActiveParent(url, submenu))
		}
	}

	determineIfActiveParent(url: string, sideNavItem: SideNavItem) {
		if (sideNavItem.link && url === this.normalizeUrl(sideNavItem.link)) {
			this.isActive = true

			if (this.afterViewInit) {
				this.expand()
			}

			this.changeDetectorRef.markForCheck()
		}

		if (sideNavItem.submenu) {
			sideNavItem.submenu.forEach(submenu => this.determineIfActiveParent(url, submenu))
		}
	}

	private normalizeUrl(url: string): string {
		if (!url) return url

		const urlWithoutQuery = url.split('?')[0]
		const urlWithoutHash = urlWithoutQuery.split('#')[0]

		return urlWithoutHash.length > 1 ? urlWithoutHash.replace(/\/+$/, '') : urlWithoutHash
	}

	toggle() {
		if (this.sideNavItem.external) {
			this.closeSideNavOnSmallScreens()
			window.open(this.sideNavItem.link, '_blank')
			return
		}

		if (this.sideNavItem.link) {
			this.sideNavService.saveCache(this.hierarchyExtension, this.sideNavItem.link)
			this.closeSideNavOnSmallScreens()
			return this.router.navigate([this.sideNavItem.link])
		}

		if (this.collapsed) {
			return this.expand()
		}

		return this.collapse()
	}

	private closeSideNavOnSmallScreens() {
		const BOOTSTRAP_LG_WIDTH = 992
		if (typeof window !== 'undefined' && window.innerWidth < BOOTSTRAP_LG_WIDTH) {
			// In this layout, removing `sidenav-toggled` closes the mobile sidenav.
			this.navigationService.openedSideNav()
		}
	}

	collapse() {
		this.sideNavService.setExpanded(this.id, false)

		const navHeight = this.navElement.scrollHeight
		const elementTransition = this.navElement.style.transition
		this.navElement.style.transition = ''

		requestAnimationFrame(() => {
			this.navElement.style.height = navHeight + 'px'
			this.navElement.style.transition = elementTransition
			requestAnimationFrame(() => {
				this.navElement.style.height = 0 + 'px'
				this.collapsed = true
				this.changeDetectorRef.markForCheck()
			})
		})
	}

	expand() {
		this.sideNavService.setExpanded(this.id, true)
		this.navElement.style.height = this.navElement.scrollHeight + 'px'
		this.collapsed = false
		this.sideNavService.expand$.next(this.hierarchyExtension)
	}

	get iconProp(): IconProp | undefined {
		if (this.sideNavItem.icon) {
			if (typeof this.sideNavItem.icon === 'string') return this.sideNavItem.icon as IconName
			else return [this.sideNavItem.icon[0] as IconPrefix, this.sideNavItem.icon[1] as IconName]
		}
	}
}
